import { useState } from 'react';
import { JobMatchAnalysis } from '../../types/resumeTuning';
import { GapInterviewModal } from './GapInterviewModal';
import './MatchAnalysisPanel.css';

interface MatchAnalysisPanelProps {
  matchAnalysis: JobMatchAnalysis;
}

interface RowState {
  open: boolean;
  saved: boolean;
}

export function MatchAnalysisPanel({ matchAnalysis }: MatchAnalysisPanelProps) {
  const [rowStates, setRowStates] = useState<Record<number, RowState>>({});

  const getRow = (idx: number): RowState =>
    rowStates[idx] || { open: false, saved: false };

  const patchRow = (idx: number, patch: Partial<RowState>) =>
    setRowStates((prev) => ({ ...prev, [idx]: { ...getRow(idx), ...patch } }));

  const getRelevanceColor = (score: number): string => {
    if (score >= 80) return '#27ae60';
    if (score >= 60) return '#f39c12';
    if (score >= 40) return '#e67e22';
    return '#e74c3c';
  };

  const getRelevanceLabel = (score: number): string => {
    if (score >= 80) return 'Excellent Match';
    if (score >= 60) return 'Good Match';
    if (score >= 40) return 'Fair Match';
    return 'Needs Work';
  };

  const strengthMeta = (strength: 'strong' | 'partial' | 'gap') => {
    if (strength === 'strong') return { icon: '✓', label: 'Strong' };
    if (strength === 'partial') return { icon: '~', label: 'Partial' };
    return { icon: '✗', label: 'Gap' };
  };

  const missingHint = (strength: 'strong' | 'partial' | 'gap', missingReason?: string | null) => {
    if (strength === 'strong') return null;
    if (missingReason) return missingReason;
    if (strength === 'partial') return 'Partial match — a voice interview may surface stronger evidence.';
    return 'No evidence found — a short voice interview can capture what you know.';
  };

  const severityMeta = (sev?: 'critical' | 'moderate' | 'minor') => {
    if (sev === 'critical') return { label: 'Critical gap', color: '#e74c3c' };
    if (sev === 'minor') return { label: 'Minor gap', color: '#27ae60' };
    return { label: 'Moderate gap', color: '#f39c12' };
  };

  const skillsMatchPercentage =
    matchAnalysis.totalRequiredSkills > 0
      ? Math.round((matchAnalysis.matchedSkills / matchAnalysis.totalRequiredSkills) * 100)
      : 0;

  const bindings = matchAnalysis.skillBindings || [];

  // Find any open modal row so we can render the modal above the table
  const openIdx = Object.entries(rowStates).find(([, s]) => s.open)?.[0];
  const openBinding = openIdx != null ? bindings[Number(openIdx)] : null;

  return (
    <div className="match-analysis-panel">
      <div className="analysis-header">
        <h2>Match Analysis</h2>
        <p>
          How well your resume aligns with this position
          {matchAnalysis.aiGenerated && <span className="ai-badge-inline"> — AI-powered</span>}
        </p>
      </div>

      {/* Overall score */}
      <div className="match-score-section">
        <div className="score-display">
          <div
            className="score-circle"
            style={{
              borderColor: getRelevanceColor(matchAnalysis.overallRelevance),
              backgroundColor: `${getRelevanceColor(matchAnalysis.overallRelevance)}20`,
            }}
          >
            <div className="score-value">{matchAnalysis.overallRelevance}%</div>
          </div>
          <div className="score-info">
            <h3>{getRelevanceLabel(matchAnalysis.overallRelevance)}</h3>
            <p>Overall relevance to the position</p>
          </div>
        </div>

        <div className="skills-match">
          <h3>Skills Match</h3>
          <div className="skills-stat">
            <div className="skills-count">
              {matchAnalysis.matchedSkills} of {matchAnalysis.totalRequiredSkills} required skills
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${skillsMatchPercentage}%`,
                  backgroundColor: getRelevanceColor(skillsMatchPercentage),
                }}
              />
            </div>
            <div className="skills-percentage">{skillsMatchPercentage}% match</div>
          </div>
        </div>
      </div>

      {/* Requirement-to-evidence binding table */}
      {bindings.length > 0 && (
        <div className="skill-bindings-section">
          <h3>Requirement Breakdown</h3>
          <p className="skill-bindings-desc">
            Each job requirement matched against your career memory and resume evidence. Use{' '}
            <strong>🎙 Interview</strong> on gaps — a short voice conversation captures what you know and saves it as a career memory for future Deep Tailor runs.
          </p>
          <table className="skill-bindings-table">
            <thead>
              <tr>
                <th>Job Requirement</th>
                <th className="col-evidence">Your Evidence</th>
                <th>Fit</th>
                <th>What's Missing</th>
              </tr>
            </thead>
            <tbody>
              {bindings.map((b, idx) => {
                const { icon, label } = strengthMeta(b.strength);
                const hint = missingHint(b.strength, b.missingReason);
                const s = getRow(idx);

                return (
                  <tr key={idx} className={`binding-row binding-${b.strength}`}>
                    <td className="binding-requirement">{b.jobRequirement}</td>
                    <td className="binding-evidence col-evidence">
                      {b.resumeEvidence ? (
                        b.resumeEvidence
                      ) : (
                        <span className="binding-no-evidence">Not evidenced in your career memory</span>
                      )}
                    </td>
                    <td className="binding-strength">
                      <span className={`strength-badge strength-badge--${b.strength}`} title={label}>
                        {icon} {label}
                      </span>
                    </td>
                    <td className="binding-missing">
                      {b.strength === 'strong' ? (
                        <span className="missing-covered">✓ Well covered</span>
                      ) : s.saved ? (
                        <span className="missing-saved">✓ Memory saved</span>
                      ) : (
                        <div className="missing-cell">
                          {b.gapSeverity && (
                            <span
                              className={`gap-severity-badge gap-severity-${b.gapSeverity}`}
                            >
                              {severityMeta(b.gapSeverity).label}
                            </span>
                          )}
                          <span className="missing-hint">{hint}</span>
                          {b.transferableSkills && b.transferableSkills.length > 0 && (
                            <div className="transferable-skills">
                              <span className="transferable-label">Could bridge with:</span>
                              {b.transferableSkills.map((sk) => (
                                <span key={sk} className="transferable-pill">{sk}</span>
                              ))}
                            </div>
                          )}
                          {b.interviewQuestions && b.interviewQuestions.length > 0 && (
                            <details className="interview-questions-preview">
                              <summary className="interview-questions-toggle">
                                Suggested questions ({b.interviewQuestions.length})
                              </summary>
                              <ol className="interview-questions-list">
                                {b.interviewQuestions.map((q, qi) => (
                                  <li key={qi}>{q}</li>
                                ))}
                              </ol>
                            </details>
                          )}
                          <button
                            type="button"
                            className="btn-add-memory"
                            onClick={() => patchRow(idx, { open: true })}
                          >
                            🎙 Interview
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Gap interview modal — rendered outside the table to avoid DOM nesting issues */}
      {openBinding && openIdx != null && (
        <GapInterviewModal
          requirement={openBinding.jobRequirement}
          strength={openBinding.strength as 'gap' | 'partial'}
          evidence={openBinding.resumeEvidence || undefined}
          interviewQuestions={openBinding.interviewQuestions}
          onSaved={() => patchRow(Number(openIdx), { saved: true })}
          onClose={() => patchRow(Number(openIdx), { open: false })}
        />
      )}

      {/* Strengths / gaps / recommendations */}
      <div className="analysis-details">
        {matchAnalysis.strengths.length > 0 && (
          <div className="detail-section strengths">
            <h3>
              <span className="section-icon">✓</span> Your Strengths
            </h3>
            <ul>
              {matchAnalysis.strengths.map((strength, idx) => (
                <li key={idx}>{strength}</li>
              ))}
            </ul>
          </div>
        )}

        {matchAnalysis.skillGaps.length > 0 && (
          <div className="detail-section gaps">
            <h3>
              <span className="section-icon">!</span> Skill Gaps
            </h3>
            <ul>
              {matchAnalysis.skillGaps.map((gap, idx) => (
                <li key={idx}>{gap}</li>
              ))}
            </ul>
            <p className="gap-note">
              Consider highlighting transferable skills or noting gaps to address
            </p>
          </div>
        )}

        {matchAnalysis.recommendations.length > 0 && (
          <div className="detail-section recommendations">
            <h3>
              <span className="section-icon">💡</span> Recommendations
            </h3>
            <ul>
              {matchAnalysis.recommendations.map((rec, idx) => (
                <li key={idx}>{rec}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="analysis-footer">
        <p className="analysis-note">
          {matchAnalysis.aiGenerated
            ? 'Analysis generated by AI based on your career memory graph and the job description.'
            : 'This analysis is based on comparing your resume skills with the job requirements.'}
        </p>
      </div>
    </div>
  );
}
